/**
 * The api's answer to "may files be added to this transfer?" (kitecorp/transferify-api-service#220),
 * served on every `TransferDto` and mirrored here as a string union — the wire values ARE the Java enum
 * names (`TransferFileAdditionState`), so no boundary mapping is needed (unlike {@link ./ExpiresAfter},
 * whose app-facing form is numeric). Same precedent as {@link ./TransferRecoveryState}.
 *
 * This is the SAME value `POST /transfer/{id}/files` switches on to refuse, which is the whole reason it
 * is served: the affordance and the verdict read one field, so they cannot disagree. The ui must NEVER
 * re-derive it from transport/encryption/completeness/expiry facts — a second copy of that grammar is
 * exactly what drifts into an "Add files" button that 400s.
 *
 * It answers transfer SHAPE only. The `editAfterSending` ENTITLEMENT stays a plan-catalogue read on the
 * client, exactly as for the message/recipient edits, so the add affordance needs BOTH.
 */
export const TransferFileAdditionState = {
	/** A live, complete, hosted transfer that is plaintext or server-escrow: files may be added. */
	SUPPORTED: "SUPPORTED",
	/**
	 * Can NEVER accept files, whatever the plan: peer-to-peer (no object storage to add to),
	 * Zero-Knowledge (the one fragment key lives only in the recipient's link, so added bytes could
	 * never be decrypted) or reminder-bound (a file request, whose contents the sender does not own).
	 * Structural and permanent — the ui states the reason and deliberately shows no upgrade CTA.
	 */
	UNSUPPORTED: "UNSUPPORTED",
	/** The transfer's own upload has not finished, so there is nothing complete to extend. */
	INCOMPLETE: "INCOMPLETE",
	/** The download link has already lapsed. */
	EXPIRED: "EXPIRED",
} as const;

export type TransferFileAdditionState =
	typeof TransferFileAdditionState[keyof typeof TransferFileAdditionState];
